// @flow

import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import { compose } from "redux";
import classNames from "classnames";
import { route } from "@routes/helper";
import { push } from "connected-react-router";

import actionCreators from "@modules/service/actionCreator";
import type { StandardQuestion } from "@modules/service/reducer/standard-questions/types";
import type { State } from "@store/reducers";
import * as serviceSelectors from "@modules/service/selectors";
import { Icons, ButtonGroup, Button, GSGrid, View, Text, Loader } from "@components";
import { AddStandardQuestionWizardModal } from "@modules/service/components";
import { formatDateTime } from "@transformers/date";

type PropsFromState = $Exact<{
  list: Array<StandardQuestion>,
  showAddStandardQuestionModal: boolean,
  modalStep: number,
  isLoading: boolean,
}>;

type PropsFromDispatch = $Exact<{
  getStandardQuestions: typeof actionCreators.GetStandardQuestions,
  setShowAddStandardQuestionModal: typeof actionCreators.SetShowAddStandardQuestionModal,
  newStandardQuestion: typeof actionCreators.NewStandardQuestion,
  setStandardQuestionAssignments: typeof actionCreators.SetStandardQuestionAssignments,
  push: typeof push,
}>;

type Props = $Exact<{
  sectionName: string,
  clientId: number,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

type FilterState = "ALL" | "ACTIVE" | "INACTIVE";

type ComponentState = $Exact<{
  filter: FilterState,
}>;

export function StandardQuestionsSection(props: Props) {
  const [filter, setFilter]: [FilterState, Function] = useState("ACTIVE");

  const columns = [
    {
      key: "id",
      name: props.t("ID"),
      align: "center",
      width: 80,
    },
    {
      key: "standardQuestion",
      name: props.t("QUESTION"),
      align: "left",
      renderCell: ({ row }: { row: StandardQuestion }) => row.content.standardQuestion.name,
    },
    {
      name: props.t("DATE_ADDED"),
      key: "createdAt",
      width: 100,
      renderCell: ({ row }: { row: StandardQuestion }) => GSGrid.render.date(row.createdAt),
    },
    {
      name: props.t("ADDED_BY"),
      key: "addedBy",
      width: 150,
      renderCell: ({ row }: { row: StandardQuestion }) =>
        GSGrid.render.ellipsis(
          row.createdBy ? `${row.createdBy.firstName} ${row.createdBy.lastName}` : "",
        ),
      align: "left",
    },
    {
      key: "status",
      name: props.t("STATUS"),
      resizable: false,
      width: 100,
      renderCell: ({ row }: { row: StandardQuestion }) =>
        GSGrid.render.statusPill(props.t(row.content.isActive ? "ACTIVE" : "INACTIVE")),
    },
    {
      key: "actions",
      width: 50,
      resizable: false,
      name: props.t("VIEW"),
      onCellClick: row => openStandardQuestionDetails(row.id),
      renderCell: () => GSGrid.render.materialIcon("search"),
    },
  ];

  useEffect(() => {
    props.getStandardQuestions({
      clientId: props.clientId,
      isActive: filter !== "ALL" ? filter == "ACTIVE" : null,
    });
  }, [filter, props.clientId]);

  function openStandardQuestionDetails(id: number) {
    props.push(
      route(`ADMINISTRATION_SERVICE_STANDARD_QUESTION_DETAILS`, {
        clientId: props.clientId,
        standardQuestionId: id,
      }),
    );
  }

  function handleAddStandardQuestion() {
    props.newStandardQuestion();
    props.setStandardQuestionAssignments([]);
    props.setShowAddStandardQuestionModal({ show: true, step: 1 });
  }

  function handleChangeFilter(filter: FilterState) {
    return function() {
      setFilter(filter);
    };
  }

  return (
    <View className="gs-container">
      {props.isLoading && <Loader />}
      <View margin="0 0 1rem 0">
        <Text type="h6" text={props.t("STANDARD_QUESTIONS")} />
      </View>
      <View justifyContent="space-between" direction="row" margin={[0, 0, "1rem", 0]}>
        <View direction="row">
          <ButtonGroup>
            <Button
              color={filter === "ALL" ? "selected" : null}
              onClick={handleChangeFilter("ALL")}
            >
              {props.t("ALL")}
            </Button>
            <Button
              color={filter === "ACTIVE" ? "selected" : null}
              onClick={handleChangeFilter("ACTIVE")}
            >
              {props.t("ACTIVE")}
            </Button>
            <Button
              color={filter === "INACTIVE" ? "selected" : null}
              onClick={handleChangeFilter("INACTIVE")}
            >
              {props.t("INACTIVE")}
            </Button>
          </ButtonGroup>
        </View>
        <View direction="row">
          <Button materialIcon="add_circle" onClick={handleAddStandardQuestion} size="small">
            {props.t("ADD")}
          </Button>
        </View>
      </View>
      <View>
        {props.list.length ? (
          <GSGrid columns={columns} rows={props.list} />
        ) : (
          <h4 className="text-pinkish-grey text-center mt-5">
            {props.t("STANDARD_QUESTIONS_LIVE_EMPTY")}
          </h4>
        )}
        {props.showAddStandardQuestionModal && (
          <AddStandardQuestionWizardModal
            clientId={props.clientId}
            defaultStep={props.modalStep}
            filter={filter}
          />
        )}
      </View>
    </View>
  );
}

const mapStateToProps = (state: State): PropsFromState => {
  return {
    isLoading: serviceSelectors.isLoading(state),
    list: serviceSelectors.getStandardQuestionsList(state),
    showAddStandardQuestionModal: serviceSelectors.getShowAddStandardQuestionModal(state),
    modalStep: serviceSelectors.getStandardQuestionsModalStep(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  getStandardQuestions: actionCreators.GetStandardQuestions,
  setShowAddStandardQuestionModal: actionCreators.SetShowAddStandardQuestionModal,
  newStandardQuestion: actionCreators.NewStandardQuestion,
  setStandardQuestionAssignments: actionCreators.SetStandardQuestionAssignments,
  push,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(StandardQuestionsSection);
